import { TrendingUp, Heart } from "lucide-react";

export default function RevenueShare() {
    return (
        <section
            className="relative px-6 md:px-10 py-16 md:py-24 bg-[#F1E8D5]"
            data-testid="revenue-share-section"
        >
            <div className="max-w-7xl mx-auto reveal">
                <div className="relative rounded-3xl bg-[#D4A017] text-black p-8 md:p-16 overflow-hidden border-2 border-black shadow-[10px_10px_0_0_#0A0A0A]">
                    <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full border-[6px] border-black/15" aria-hidden />
                    <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full bg-[#C2410C]" aria-hidden />

                    <div className="relative grid md:grid-cols-12 gap-10 items-start">
                        <div className="md:col-span-8">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-black text-[#D4A017] border-2 border-black">
                                <TrendingUp size={20} />
                            </div>
                            <h2 className="mt-5 font-display tracking-tighter font-black leading-[0.95] text-5xl md:text-6xl lg:text-[5.5rem]">
                                Your journey
                                <br />
                                <em className="not-italic bg-black text-[#D4A017] px-3 -rotate-1 inline-block">continues.</em>
                            </h2>

                            <div className="mt-8 space-y-5 text-black/85 text-base md:text-lg leading-relaxed">
                                <p>
                                    Releasing your first single isn&rsquo;t the
                                    end of your journey&mdash;it&rsquo;s the
                                    beginning of an exciting new chapter.
                                </p>
                                <p>
                                    Once your song has been released, it will
                                    be available across the world&rsquo;s
                                    leading music streaming platforms, ready
                                    to be shared with family, friends and
                                    listeners around the globe.
                                </p>
                                <p>
                                    As people begin discovering your music,
                                    you&rsquo;ll be able to follow your
                                    song&rsquo;s progress, seeing how many
                                    times it&rsquo;s been streamed and
                                    celebrating every milestone along the way.
                                </p>
                                <p>
                                    Whether it&rsquo;s your first 100 streams,
                                    your first 1,000, or hearing that someone
                                    you&rsquo;ve never met has listened to
                                    your music, every achievement is something
                                    to be proud of.
                                </p>
                                <p>
                                    And if releasing your first single inspires
                                    you to create another, your My First Single
                                    vocal coach will be there to guide you
                                    through the journey once again.
                                </p>
                            </div>
                        </div>

                        <aside className="md:col-span-4 md:sticky md:top-24 self-start bg-black text-white rounded-3xl p-7 md:p-8 border-2 border-black shadow-[5px_5px_0_0_#C2410C] relative overflow-hidden">
                            <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-[#C2410C] opacity-25 blur-3xl" aria-hidden />
                            <div className="relative space-y-4 font-display font-black tracking-tighter text-3xl md:text-4xl leading-[1.05]">
                                <div className="text-white">
                                    Because every song tells a story.
                                </div>
                                <div className="text-[#D4A017]">
                                    And every new song is the start of another.
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>

                {/* Closing block — Not sure if it's right for you */}
                <div
                    className="mt-8 md:mt-10 bg-[#1B4D3E] text-white rounded-3xl p-8 md:p-12 border-2 border-black shadow-[8px_8px_0_0_#0A0A0A] reveal"
                    data-testid="journey-closing"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <span className="w-11 h-11 rounded-2xl bg-[#D4A017] text-black border-2 border-black flex items-center justify-center">
                            <Heart size={20} fill="currentColor" />
                        </span>
                        <h3 className="font-display font-black tracking-tighter text-2xl md:text-3xl">
                            Not sure if it&rsquo;s right for you?
                        </h3>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-4 text-white/85 text-base leading-relaxed">
                            <p className="font-display font-bold text-lg text-white">
                                That&rsquo;s perfectly normal.
                            </p>
                            <p>
                                Many of our students begin with nothing more than
                                a dream and the desire to do something
                                they&rsquo;ve always wanted to do.
                            </p>
                            <ul className="space-y-1.5 text-lg font-display font-bold tracking-tight text-white pt-1">
                                <li>You don&rsquo;t need to have written a song.</li>
                                <li>You don&rsquo;t need recording experience.</li>
                                <li>And you certainly don&rsquo;t need to be a professional singer.</li>
                            </ul>
                        </div>
                        <div className="text-white/85 text-base leading-relaxed">
                            <p>
                                The best place to start is with a{" "}
                                <span className="text-[#D4A017] font-bold">free 20&#8209;minute consultation</span>{" "}
                                with your local My First Single vocal coach.
                            </p>
                            <p className="mt-3">
                                It&rsquo;s a relaxed, no&#8209;obligation
                                conversation where you can talk about your
                                musical interests, your ideas and what
                                you&rsquo;d like to achieve. You&rsquo;ll have
                                the opportunity to ask questions, explore how
                                the journey works and decide whether My First
                                Single is the right experience for you.
                            </p>
                            <p className="mt-3">
                                There&rsquo;s no pressure and no commitment
                                &mdash; just an opportunity to discover
                                what&rsquo;s possible.
                            </p>
                            <p className="mt-4 font-display font-bold text-xl text-[#D4A017]">
                                Because every great song begins with a conversation.
                            </p>
                            <a
                                href="#teachers"
                                className="mt-6 inline-flex items-center justify-center gap-2 bg-[#D4A017] hover:bg-white text-black font-medium rounded-full px-6 py-3.5 border-2 border-black transition-all active:scale-95"
                                data-testid="journey-cta"
                            >
                                Book your free 20&#8209;minute consultation
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
